import Box from '@mui/material/Box';
import BuildIcon from '@mui/icons-material/Build';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import type { ReactNode } from 'react';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhComplianceControlItem } from 'src/types/mindtime/wazuhCompliance';
import {
  EDR_COLORS,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrStatusPill,
  formatEdrDateTime,
  type EdrDetailKeyValue,
  type EdrEntityDetailPageProps,
  type EdrStatusTone
} from '../shared/edrPrimitives';

const STATUS_LABELS: Record<string, string> = {
  pass: 'Pass',
  fail: 'Fail',
  needs_review: 'Needs Review',
};

const normalizeKey = (value?: string | null) => (value ?? '').trim().toLowerCase();
const hasText = (value?: string | null): value is string => Boolean(value?.trim());

export const toComplianceTitle = (value?: string | null): string => {
  const label = STATUS_LABELS[normalizeKey(value)];
  if (label) return label;
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const complianceStatusToTone = (status?: string | null): EdrStatusTone => {
  const key = normalizeKey(status);
  if (key === 'pass') return 'success';
  if (key === 'fail') return 'critical';
  return 'medium';
};

/** Id for GET /compliance/controls/{id} — prefers `id`, then `_id`. */
export const getComplianceControlId = (
  item: Pick<WazuhComplianceControlItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

const TextCard = ({ icon, title, text }: { icon: ReactNode; title: string; text?: string | null }) =>
  hasText(text) ? (
    <EdrDetailSectionCard icon={icon} title={title}>
      <EdrExpandableText text={text} />
    </EdrDetailSectionCard>
  ) : null;

const FrameworkTags = ({ values }: { values: string[] }) =>
  values.length ? (
    <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
      {values.map((value) => (
        <span
          key={value}
          style={{
            display: 'inline-block',
            padding: '2px 7px',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            background: EDR_COLORS.slate100,
            color: EDR_COLORS.slate700,
            border: `1px solid ${EDR_COLORS.slate200}`,
          }}
        >
          {value}
        </span>
      ))}
    </Box>
  ) : null;

export const buildComplianceDetailProps = (
  item: WazuhComplianceControlItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const statusLabel = toComplianceTitle(item.status);
  const statusTone = complianceStatusToTone(item.status);
  const frameworks = (item.framework ?? []).filter(Boolean);
  const resultFailed = ['fail', 'failed'].includes(normalizeKey(item.result));
  const iconSx = { fontSize: 16 };

  const detailRows: EdrDetailKeyValue[] = [
    { label: 'Control Name', value: item.title },
    { label: 'Check ID', value: item.checkId, mono: true },
    { label: 'Framework', value: <FrameworkTags values={frameworks} /> },
    { label: 'Category', value: item.category },
    { label: 'Policy', value: item.policyName },
    { label: 'Policy ID', value: item.policyId, mono: true },
    { label: 'Endpoint', value: item.endpoint },
    { label: 'Organization', value: item.organization },
    {
      label: 'Result',
      value: hasText(item.result) ? (
        <span style={{ color: resultFailed ? EDR_COLORS.critical : undefined }}>{item.result}</span>
      ) : undefined,
    },
    { label: 'Condition', value: item.condition },
    { label: 'Last Checked', value: item.lastChecked ? formatEdrDateTime(item.lastChecked) : undefined },
  ];

  const targetRows: EdrDetailKeyValue[] = [
    { label: 'Target', value: item.target },
    { label: 'Registry', value: item.registry, mono: true },
    { label: 'File', value: item.file, mono: true },
    { label: 'Process', value: item.process, mono: true },
    { label: 'Directory', value: item.directory, mono: true },
    { label: 'Command', value: item.command, mono: true },
  ];

  const complianceRows: EdrDetailKeyValue[] = (item.compliance ?? [])
    .filter((row) => hasText(row?.key) && hasText(row?.value))
    .map((row) => ({ label: row.key, value: row.value, mono: true }));

  // const rules = (item.rules ?? []).filter((row) => hasText(row?.rule));
  const meta = [frameworks.join(' · ') || null, item.category, item.endpoint].filter(Boolean).join(' · ');
  const hasTarget = [item.target, item.registry, item.file, item.process, item.directory, item.command].some(hasText);

  return {
    backLink: {
      label: 'Back to Configuration Compliance',
      to: getSecurityNavItemPath('configuration-assessment') ?? '',
    },
    title: item.title || item.checkId,
    metaLine: (
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
        <span>{meta || '—'}</span>
        <EdrStatusPill tone={statusTone} label={statusLabel} />
      </Box>
    ),
    main: (
      <>
        <TextCard icon={<InfoOutlinedIcon style={iconSx} />} title="Description" text={item.description} />
        <TextCard icon={<InfoOutlinedIcon style={iconSx} />} title="Rationale" text={item.rationale} />
        <EdrDetailSectionCard icon={<SecurityIcon style={iconSx} />} title="Control Details">
          <EdrDetailKeyValueRows rows={detailRows} />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <>
        {hasTarget ? (
          <EdrDetailSectionCard icon={<SettingsIcon style={iconSx} />} title="Target">
            <EdrDetailKeyValueRows rows={targetRows} />
          </EdrDetailSectionCard>
        ) : null}
        <TextCard icon={<BuildIcon style={iconSx} />} title="Remediation" text={item.remediation} />
        {complianceRows.length > 0 ? (
          <EdrDetailSectionCard icon={<VerifiedUserIcon style={iconSx} />} title="Compliance Mappings">
            <EdrDetailKeyValueRows rows={complianceRows} />
          </EdrDetailSectionCard>
        ) : null}
        {/* {rules.length > 0 ? (
          <EdrDetailSectionCard icon={<FormatListBulletedIcon style={iconSx} />} title="Rules">
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              {rules.map((rule) => (
                <Box
                  key={`${rule.type}:${rule.rule}`}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 6,
                    background: EDR_COLORS.slate50,
                    border: `1px solid ${EDR_COLORS.slate200}`,
                  }}
                >
                  {hasText(rule.type) ? (
                    <div style={{ fontSize: 10, fontWeight: 600, color: EDR_COLORS.textMuted, marginBottom: 3, textTransform: 'capitalize' }}>
                      {rule.type}
                    </div>
                  ) : null}
                  <div style={{ ...edrMonoStyle, fontSize: 11, color: EDR_COLORS.textPrimary, wordBreak: 'break-all', lineHeight: 1.45 }}>
                    {rule.rule}
                  </div>
                </Box>
              ))}
            </Box>
          </EdrDetailSectionCard>
        ) : null} */}
      </>
    ),
  };
};
